import { type AccountInfo, type AuthenticationResult, type PopupRequest, PublicClientApplication, type SilentRequest } from "@azure/msal-browser";
import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import { storeToRefs } from "pinia";
import { useIcs4Store } from '@/store/storeIcs4'
import { useGlobalStore } from '@/store/storeGlobal';
import { currentDatabase } from '@/helpers/currentDatabase';
import { useNotificationsStore } from "@/store/notificationsStore";
import { useEnvironmentStore } from '@/store/environmentStore';

// *************************************************
// API ADDRESS
// *************************************************
const debugPrefix = '[Auth]';

// Funksjon for å oppdatere environment store og global store etter Pinia er aktivert
export function updateEnvironmentStore (config: ConfigCache) {
  try {
    const environmentStore = useEnvironmentStore();
    environmentStore.setConfigCache(config);
    environmentStore.setEnvironmentName(config.VITE_ENVIRONMENT || 'DEV');
    console.log(`${debugPrefix} Environment store oppdatert med:`, config.VITE_ENVIRONMENT);

    // Oppdater global store med build info
    const globalStore = useGlobalStore();
    globalStore.setBuildInfo(config.BUILD_VERSION || '', config.BUILD_DATE || '');
    console.log(`${debugPrefix} Global store oppdatert med build info:`, config.BUILD_VERSION, config.BUILD_DATE);
  } catch (error) {
    console.warn(`${debugPrefix} Kunne ikke oppdatere stores:`, error);
  }
}

// Type definitions
export interface ConfigCache {
  VITE_ENVIRONMENT: string;
  VITE_API_URL: string;
  VITE_MSAL_CLIENT_ID: string;
  VITE_MSAL_AUTHORITY: string;
  VITE_MSAL_SCOPES: string | string[];
  BUILD_VERSION?: string;
  BUILD_DATE?: string;
}

interface MsalConfig {
  auth: {
    clientId: string;
    authority: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    navigateToLoginRequestUrl: boolean;
  };
  cache: {
    cacheLocation: "localStorage";
    storeAuthStateInCookie: boolean;
  };
}

// Cache for konfigurasjonsverdier
let configCache: ConfigCache | null = null;
let configPromise: Promise<ConfigCache> | null = null;
let environment: string | null = null;

// Funksjon for å hente default-konfigurasjon fra Vite environment variabler (.env)
function getEnvConfig (): ConfigCache {
  return {
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || 'DEV',
    VITE_API_URL: import.meta.env.VITE_API_URL || import.meta.env.VITE_API_URI_DEV?.replace(/\/api\/?$/, '') || 'https://localhost:7047',
    VITE_MSAL_CLIENT_ID: import.meta.env.VITE_MSAL_CLIENT_ID || '',
    VITE_MSAL_AUTHORITY: import.meta.env.VITE_MSAL_AUTHORITY || '',
    VITE_MSAL_SCOPES: import.meta.env.VITE_MSAL_SCOPES || ''
  };
}

// Normaliserer API-respons til ConfigCache format
// Håndterer camelCase (apiUrl), PascalCase (ApiUrl) og VITE_-prefix varianter
function normalizeApiResponse (data: Record<string, unknown>): ConfigCache | null {
  // Prøv camelCase/PascalCase først (ny standard fra .NET API)
  const apiUrl = (data.apiUrl || data.ApiUrl) as string | undefined;
  const msalClientId = (data.msalClientId || data.MsalClientId) as string | undefined;
  const msalAuthority = (data.msalAuthority || data.MsalAuthority) as string | undefined;
  const msalScopes = (data.msalScopes || data.MsalScopes) as string | string[] | undefined;
  const env = (data.environment || data.Environment) as string | undefined;
  const buildVersion = (data.buildVersion || data.BuildVersion) as string | undefined;
  const buildDate = (data.buildDate || data.BuildDate) as string | undefined;

  if (apiUrl && msalClientId && msalAuthority) {
    return {
      VITE_API_URL: apiUrl,
      VITE_ENVIRONMENT: env || 'unknown',
      VITE_MSAL_CLIENT_ID: msalClientId,
      VITE_MSAL_AUTHORITY: msalAuthority,
      VITE_MSAL_SCOPES: msalScopes || '',
      BUILD_VERSION: buildVersion,
      BUILD_DATE: buildDate
    };
  }

  // Fallback til VITE_-prefix (legacy SWA function)
  const viteApiUrl = (data.VITE_API_URL || data.vite_API_URL) as string | undefined;
  const viteMsalClientId = (data.VITE_MSAL_CLIENT_ID || data.vite_MSAL_CLIENT_ID) as string | undefined;
  const viteMsalAuthority = (data.VITE_MSAL_AUTHORITY || data.vite_MSAL_AUTHORITY) as string | undefined;
  const viteMsalScopes = (data.VITE_MSAL_SCOPES || data.vite_MSAL_SCOPES) as string | string[] | undefined;
  const viteEnv = (data.VITE_ENVIRONMENT || data.vite_ENVIRONMENT) as string | undefined;
  // Build info: sjekk både camelCase (SWA function serialiserer med camelCase) og VITE_ prefix
  const viteBuildVersion = (data.buildVersion || data.BuildVersion || data.VITE_BUILD_VERSION || data.vite_BUILD_VERSION) as string | undefined;
  const viteBuildDate = (data.buildDate || data.BuildDate || data.VITE_BUILD_DATE || data.vite_BUILD_DATE) as string | undefined;

  if (viteApiUrl && viteMsalClientId && viteMsalAuthority) {
    return {
      VITE_API_URL: viteApiUrl,
      VITE_ENVIRONMENT: viteEnv || 'unknown',
      VITE_MSAL_CLIENT_ID: viteMsalClientId,
      VITE_MSAL_AUTHORITY: viteMsalAuthority,
      VITE_MSAL_SCOPES: viteMsalScopes || '',
      BUILD_VERSION: viteBuildVersion,
      BUILD_DATE: viteBuildDate
    };
  }

  return null;
}

async function getEnvironmentVariables (): Promise<ConfigCache> {
  // Returner cachet verdi hvis tilgjengelig
  if (configCache !== null) {
    return configCache;
  }

  // Returner eksisterende promise hvis en forespørsel allerede er i gang
  if (configPromise !== null) {
    return configPromise;
  }

  // Opprett en ny promise for API-kallet
  configPromise = new Promise<ConfigCache>(async (resolve) => {
    try {
      console.log(`${debugPrefix} Henter konfigurasjon fra /api/config...`);
      const response = await axios.get('/api/config');

      // Normaliser API-responsen
      const normalized = normalizeApiResponse(response.data);

      if (normalized) {
        configCache = normalized;
        environment = normalized.VITE_ENVIRONMENT;
        console.log(`${debugPrefix} Konfigurasjon hentet fra API:`, {
          environment: normalized.VITE_ENVIRONMENT,
          apiUrl: normalized.VITE_API_URL,
          clientId: normalized.VITE_MSAL_CLIENT_ID.substring(0, 8) + '...'
        });
        resolve(normalized);
        return;
      }

      console.warn(`${debugPrefix} API-respons mangler påkrevde felter, faller tilbake til .env`, response.data);
    } catch (error) {
      console.warn(`${debugPrefix} Kunne ikke hente konfigurasjon fra API:`, error);
    }

    // Fallback til .env
    const envConfig = getEnvConfig();
    if (envConfig.VITE_MSAL_CLIENT_ID && envConfig.VITE_MSAL_AUTHORITY) {
      configCache = envConfig;
      environment = envConfig.VITE_ENVIRONMENT;
      console.log(`${debugPrefix} Bruker konfigurasjon fra .env:`, {
        environment: envConfig.VITE_ENVIRONMENT,
        apiUrl: envConfig.VITE_API_URL,
        clientId: envConfig.VITE_MSAL_CLIENT_ID.substring(0, 8) + '...'
      });
      resolve(envConfig);
    } else {
      console.error(`${debugPrefix} Ingen gyldig konfigurasjon tilgjengelig`);
      // Resolve med tom config - vil feile senere med bedre feilmelding
      resolve(envConfig);
    }

    configPromise = null;
  });

  return configPromise;
}

// Fjern environment og baseURL initialisering
let baseURL: string | null = null;
let hubURL: string | null = null;

// Ikke initialiser axiosClient direkte
export let axiosClient: AxiosInstance | null = null;

// Holder status for initialisering
let initComplete = false;
let initError: string | null = null;
let initPromise: Promise<void> | null = null;

// Funksjon for å sjekke om auth er initialisert
function checkInit (): void {
  if (!initComplete) {
    if (initError) {
      throw new Error(`Auth er ikke initialisert på grunn av: ${initError}`);
    } else {
      throw new Error('Auth er ikke initialisert enda, vent på initAuth() å fullføre');
    }
  }
}

// Initialiser auth - må kalles før noe annet
async function initAuth (): Promise<void> {
  // Returner eksisterende promise hvis initialisering allerede pågår
  if (initPromise) {
    return initPromise;
  }

  // Opprett en ny promise for initialisering
  initPromise = new Promise<void>(async (resolve, reject) => {
    try {
      console.log(`${debugPrefix} Starter initialisering av auth...`);

      // Hent konfigurasjon fra API
      const config = await getEnvironmentVariables();

      // Verifiser nødvendige konfigurasjonsverdier
      if (!config.VITE_API_URL) {
        throw new Error('VITE_API_URL mangler i konfigurasjonen');
      }

      if (!config.VITE_MSAL_CLIENT_ID || !config.VITE_MSAL_AUTHORITY) {
        throw new Error('MSAL-konfigurasjon mangler (VITE_MSAL_CLIENT_ID eller VITE_MSAL_AUTHORITY)');
      }

      // Sett environment
      environment = config.VITE_ENVIRONMENT || 'DEV';
      // useEnvironmentStore().setEnvironmentName(environment);
      // Sett baseURL
      baseURL = `${config.VITE_API_URL}/api`;
      hubURL = `${config.VITE_API_URL}/hubs/event`;

      console.log(`${debugPrefix} Konfigurasjon lastet:`, {
        environment,
        baseURL,
        clientId: config.VITE_MSAL_CLIENT_ID.substring(0, 8) + '...' // Vis bare deler av client ID av sikkerhetshensyn
      });

      // Initialiser axiosClient
      axiosClient = axios.create({ baseURL });

      // Sett opp interceptors
      setupAxiosInterceptors();

      initComplete = true;
      console.log(`${debugPrefix} Auth initialisering fullført`);

      resolve();
    } catch (error: any) {
      console.error(`${debugPrefix} Kritisk feil under initialisering:`, error);
      initError = error.message;
      reject(error);
    } finally {
      initPromise = null;
    }
  });

  return initPromise;
}

// Funksjon for å sette opp interceptors
function setupAxiosInterceptors (): void {
  if (!axiosClient) return;

  // Token-related interceptor
  axiosClient.interceptors.request.use(
    async (config: any) => {
      checkInit(); // Sjekk at auth er initialisert

      try {
        const tokenResponse = await getTokenPopup();
        if (tokenResponse?.accessToken) {
          if (config.headers) {
            config.headers["Authorization"] = `Bearer ${tokenResponse.accessToken}`;
          }
        } else {
          // Avbryt request hvis vi ikke fikk token
          const error = new Error(TOKEN_ERROR);
          return Promise.reject(error);
        }
      } catch (error) {
        console.error("Failed to acquire token:", error);
        // Avbryt request ved å returnere en rejected Promise
        return Promise.reject(error);
      }

      if (currentDatabase && config.headers) {
        config.headers['Ics-DbName'] = currentDatabase;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Global response interceptor for error handling
  axiosClient.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error: any) => {
      console.log("Error in axiosClient.interceptors.response.use: |", error.name, '|', error.message, '|', typeof error);

      // Check if this was a file download request
      const isFileDownload = error.config?.responseType === 'arraybuffer' || error.config?.responseType === 'blob';

      // For file downloads, just propagate the error as the specific handling is done elsewhere
      if (isFileDownload) {
        return Promise.reject(error);
      }

      if (error.name === "Error" && error.message === TOKEN_ERROR) {
        return false;
      }

      const notificationsStore = useNotificationsStore();
      const globalStore = useGlobalStore();

      const problem = error?.response?.data;
      if (problem) {
        // For valideringsfeil (400 Bad Request) på add-record endepunktet, la komponenten håndtere det
        if (error.response?.status === 400 && problem.errors && error.config?.url?.includes('add-record')) {
          globalStore.debug(`${debugPrefix} Validation error for add-record: `, problem);
          return Promise.reject(error);
        }

        // For andre feil, vis notification
        notificationsStore.addProblem(problem);
        globalStore.debug(`${debugPrefix} API Call failed with: `, problem);
        return false;
      }

      notificationsStore.addError("Uventet feil!", "Prøv å last inn siden på nytt, hvis problemet vedvarer, kontakt systemadministrator.");
      return Promise.reject(error);
    }
  );
}

// Wrapper for axiosClient som sjekker om auth er initialisert
function getAxiosClient (): AxiosInstance {
  checkInit();
  if (!axiosClient) {
    throw new Error('AxiosClient ikke initialisert');
  }
  return axiosClient;
}

// isUserGlobalAdmin må bruke getAxiosClient
async function isUserGlobalAdmin (): Promise<boolean> {
  const client = getAxiosClient();
  const res = await client.get('v2/user/is-global-admin');
  return res?.data ?? false;
}

function ensureTrailingSlash (url: string): string {
  if (!url.endsWith('/')) {
    console.log('ensureTrailingSlash (adding /): ', url + '/');
    return url + '/';
  }
  console.log('ensureTrailingSlash (ok): ', url);
  return url;
}

// Lager en funksjon for å hente MSAL config
async function getMsalConfig (): Promise<MsalConfig> {
  // Vent på miljøvariabler fra API
  const envVars = await getEnvironmentVariables();

  if (!envVars.VITE_MSAL_CLIENT_ID) {
    throw new Error('VITE_MSAL_CLIENT_ID mangler i konfigurasjonen');
  }

  if (!envVars.VITE_MSAL_AUTHORITY) {
    throw new Error('VITE_MSAL_AUTHORITY mangler i konfigurasjonen');
  }

  // Opprett baseUri
  const baseUri = ensureTrailingSlash(location.origin);

  // Returner config-objekt med verdier fra API
  return {
    auth: {
      clientId: envVars.VITE_MSAL_CLIENT_ID,
      authority: envVars.VITE_MSAL_AUTHORITY,
      redirectUri: baseUri,
      postLogoutRedirectUri: baseUri,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false
    }
  };
}

let msalInstance: PublicClientApplication | null = null;
let initializePromise: Promise<PublicClientApplication> | null = null;
let userAzureId = "";
let userAccount: AccountInfo | null = null;

// Legg til en Set for å tracke hvilke brukere som har fått oppdatert sine tokens
const refreshedAccounts = new Set<string>();

async function getMsalInstance (): Promise<PublicClientApplication> {
  if (!initializePromise) {
    initializePromise = initializeMsal();
  }
  return await initializePromise;
}

async function initializeMsal (): Promise<PublicClientApplication> {
  if (msalInstance) {
    return msalInstance;
  }

  try {
    console.log('Initializing MSAL');
    // Hent config fra vår nye metode
    const config = await getMsalConfig();
    msalInstance = new PublicClientApplication(config);
    await msalInstance.initialize();
    console.log('MSAL initialized');
    return msalInstance;
  } catch (error) {
    console.error("MSAL initialization failed:", error);
    throw error;
  }
}

// *************************************************
// LOGIN
async function getApiTokenRequest (): Promise<{ scopes: string[] }> {
  const config = await getEnvironmentVariables();

  if (!config.VITE_MSAL_CLIENT_ID) {
    throw new Error('VITE_MSAL_CLIENT_ID mangler i konfigurasjonen');
  }

  let scopes: string[];

  // Håndter scopes - kan være array, JSON-string, eller space-separert string
  if (Array.isArray(config.VITE_MSAL_SCOPES)) {
    // Allerede et array (fra ny API)
    scopes = config.VITE_MSAL_SCOPES;
  } else if (typeof config.VITE_MSAL_SCOPES === 'string') {
    const scopeStr = config.VITE_MSAL_SCOPES.trim();

    if (scopeStr.startsWith('[')) {
      // JSON-array string (legacy format)
      scopes = JSON.parse(scopeStr);
    } else {
      // Space-separert string
      scopes = scopeStr.split(/\s+/).filter(s => s.length > 0);
    }
  } else {
    throw new Error('VITE_MSAL_SCOPES har ugyldig format');
  }

  if (scopes.length === 0) {
    throw new Error('VITE_MSAL_SCOPES må inneholde minst ett scope');
  }

  console.log(`${debugPrefix} Bruker scopes fra konfigurasjon:`, scopes);

  return { scopes };
}

// Oppdater loginRequest til å bruke config
const loginRequest = {
  prompt: "select_account"
} as PopupRequest;

async function login (retryCount = 0): Promise<boolean> {
  const ics4Store = useIcs4Store();
  const { isAuth, userName } = storeToRefs(ics4Store);
  const notificationsStore = useNotificationsStore();

  try {
    const instance = await getMsalInstance();
    const tokenConfig = await getApiTokenRequest();

    // Mer aggressiv nullstilling av MSAL-tilstand
    if (retryCount === 0) {
      try {
        instance.setActiveAccount(null);
        await instance.clearCache();

        // Tøm relevant localStorage
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('msal.') || key.includes('login.windows')) {
            localStorage.removeItem(key);
          }
        });

        // Lengre pause for å sikre at alt er nullstilt
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (e) {
        console.warn('Failed to clear MSAL state:', e);
      }
    }

    try {
      const response = await instance.loginPopup({
        ...loginRequest,
        ...tokenConfig,
        prompt: "select_account"
      });

      if (response?.account) {
        instance.setActiveAccount(response.account);
        userAzureId = response.account.username;
        userAccount = response.account;
        userName.value = response.account?.name;
        isAuth.value = true;
        console.log('Account info:', {
          name: response.account.name,
          username: response.account.username,
          idTokenClaims: response.account.idTokenClaims
        });

        return true;
      }
    } catch (error: any) {
      if (error.name === "BrowserAuthError" && error.message.includes("hash_empty_error")) {
        if (retryCount < 1) {
          console.warn('Hash empty error, attempting retry with delay');
          await new Promise(resolve => setTimeout(resolve, 1000)); // Økt delay
          return login(retryCount + 1);
        } else {
          notificationsStore.addError(
            "Popup blokkert",
            "Det ser ut til at nettleseren din blokkerer popup-vinduet."
          );
          return false;
        }
      }

      userAccount = null;
      throw error;
    }

    return false;
  } catch (error) {
    console.error("Login error:", error);
    notificationsStore.addError(
      "Innloggingsfeil",
      "Det oppstod en feil under innlogging."
    );
    return false;
  }
}

// *************************************************
// LOGOUT
const logoutRequest = {
  //account: msalInstance.getAccountByHomeId(homeAccountId),
  mainWindowRedirectUri: "/",
};

async function logout (): Promise<void> {
  if (!msalInstance) {
    throw new Error('MSAL instance ikke initialisert');
  }
  await msalInstance.logoutPopup(logoutRequest);
  await isAuthenticated();
}

// *************************************************
// AUTH SJEKK
let authCheckInProgress = false;
let lastAuthResult: Promise<boolean> | null = null;

async function isAuthenticated (): Promise<boolean> {
  if (authCheckInProgress && lastAuthResult) {
    return lastAuthResult;
  }

  authCheckInProgress = true;
  lastAuthResult = checkAuthentication();

  try {
    return await lastAuthResult;
  } finally {
    authCheckInProgress = false;
    lastAuthResult = null;
  }
}

async function checkAuthentication (): Promise<boolean> {
  try {
    const instance = await getMsalInstance();
    const accounts = instance.getAllAccounts();

    const ics4Store = useIcs4Store();
    const { isAuth, userName } = storeToRefs(ics4Store);

    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      userAzureId = accounts[0].username;
      userName.value = accounts[0]?.name;
      userAccount = accounts[0];

      // Prøv silent token refresh for å sjekke at token er gyldig
      try {
        const tokenConfig = await getApiTokenRequest();
        await instance.acquireTokenSilent({
          account: accounts[0],
          ...tokenConfig,
          prompt: "none"
        });
        // Token er gyldig
        isAuth.value = true;
        return true;
      } catch (silentError: any) {
        // Silent refresh feilet - token er ikke gyldig eller utløpt
        console.debug('[Auth] Silent token refresh failed:', silentError);
        isAuth.value = false;
        userAccount = null;
        return false;
      }
    }
    isAuth.value = false;
    userAccount = null;
    return false;
  } catch (error) {
    console.error('Error in isAuthenticated:', error);
    userAccount = null;
    return false;
  }
}

const TOKEN_ERROR = "Kunne ikke hente gyldig token";

// *************************************************
// AXIOS OG TOKEN

let tokenRequestInProgress = false;
let lastTokenResult: Promise<AuthenticationResult | null> | null = null;
let tokenExpiresAt = 0;
let cachedToken: AuthenticationResult | null = null;
const tokenBuffer = 5 * 60 * 1000; // 5 minutter buffer

function isTokenValid (token: AuthenticationResult | null): boolean {
  return token !== null && Date.now() < (tokenExpiresAt - tokenBuffer);
}

async function getTokenPopup (): Promise<AuthenticationResult | null> {
  if (isTokenValid(cachedToken)) {
    return cachedToken;
  }

  // Eksisterende logikk for å håndtere parallelle requests
  if (tokenRequestInProgress && lastTokenResult) {
    const token = await lastTokenResult;
    if (isTokenValid(token)) {
      console.log('Using cached token, expires at:', tokenExpiresAt);
      return token;
    }
  }

  tokenRequestInProgress = true;
  lastTokenResult = requestToken();

  try {
    const token = await lastTokenResult;
    // Sett utløpstidspunkt når vi får nytt token
    if (token?.expiresOn) {
      // expiresOn skal være et Date-objekt ifølge MSAL-dokumentasjonen
      let expiresAt: number;
      if (token.expiresOn instanceof Date) {
        expiresAt = token.expiresOn.getTime();
      } else {
        // fallback hvis det mot formodning er en string
        expiresAt = new Date(token.expiresOn).getTime();
      }
      tokenExpiresAt = expiresAt;
      cachedToken = token;
      console.log('Set cached token - Token expires at:', tokenExpiresAt, new Date(tokenExpiresAt));
    }
    return token;
  } finally {
    tokenRequestInProgress = false;
    console.log('Token request completed');
  }
}

async function requestToken (): Promise<AuthenticationResult | null> {
  try {
    const instance = await getMsalInstance();
    const account = instance.getActiveAccount();
    const tokenConfig = await getApiTokenRequest();

    // Kjør alltid force refresh for å sikre oppdaterte tokens
    if (account) {
      try {
        await instance.acquireTokenSilent({
          account: account,
          ...tokenConfig,
          forceRefresh: true
        });
      } catch (error) {
        await instance.clearCache();
      }
    }

    const tokenRequest: SilentRequest = {
      account: account || undefined,
      ...tokenConfig,
      prompt: "none"
    };

    // Prøv først silent token acquisition
    try {
      if (account) {
        const silentResult = await instance.acquireTokenSilent(tokenRequest);
        if (silentResult?.accessToken) {
          return silentResult;
        }
      }
    } catch (silentError: any) {
      // Spesifikk håndtering av scope-feil
      if (silentError.message?.includes('AADSTS650053') || silentError.message?.includes('invalid_client')) {
        console.error('Ugyldig scope-konfigurasjon:', silentError);
        const notificationsStore = useNotificationsStore();
        notificationsStore.addError(
          "Autentiseringsfeil",
          "Det oppstod en feil med tilgangsrettigheter. Kontakt systemadministrator."
        );
        // Ikke prøv popup hvis vi har scope-feil
        return null;
      }
      console.debug("[Auth] Silent token acquisition failed, trying popup");
    }

    // Popup med retry-logikk hvis silent feilet
    let retryCount = 0;
    const maxRetries = 2;

    while (retryCount <= maxRetries) {
      try {
        const popupResult = await instance.acquireTokenPopup({
          ...tokenRequest,
          prompt: "select_account"
        });

        if (popupResult?.accessToken) {
          return popupResult;
        }
        break;
      } catch (error: any) {
        if (error.name === "BrowserAuthError" && error.message.includes("popup_window_error")) {
          console.debug(`[Auth] Popup window error`);
          return null;
        }
        if (error.name === "BrowserAuthError" && error.message.includes("login_required")) {
          console.debug(`[Auth] Interaction in progress`);
          return null;
        }
        if (error.name === "BrowserAuthError" && error.message.includes("interaction_in_progress")) {
          console.debug(`[Auth] Interaction in progress`);
          return null;
        }
        if (error.name === "BrowserAuthError" && error.message.includes("hash_empty_error")) {
          if (retryCount < maxRetries) {
            console.debug(`[Auth] Hash empty error, retry ${retryCount + 1} of ${maxRetries}`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            retryCount++;
            continue;
          }
        }
        throw error;
      }
    }

    throw new Error("Kunne ikke hente token");
  } catch (error: any) {
    // Spesifikk håndtering av scope-feil på toppnivå
    if (error.message?.includes('AADSTS650053') || error.message?.includes('invalid_client')) {
      console.error('Ugyldig scope-konfigurasjon:', error);
      const notificationsStore = useNotificationsStore();
      notificationsStore.addError(
        "Autentiseringsfeil",
        "Det oppstod en feil med tilgangsrettigheter. Kontakt systemadministrator."
      );
      return null;
    }
    console.error("Token acquisition failed:", error);
    throw error;
  }
}

// Hent token kun via silent refresh (for SignalR - unngår popup)
async function getTokenSilent (): Promise<AuthenticationResult | null> {
  // Sjekk om cached token er gyldig
  if (isTokenValid(cachedToken)) {
    return cachedToken;
  }

  try {
    const instance = await getMsalInstance();
    const account = instance.getActiveAccount();
    const tokenConfig = await getApiTokenRequest();

    if (!account) {
      console.warn('[Auth] Ingen account tilgjengelig for silent token refresh');
      return null;
    }

    const tokenRequest: SilentRequest = {
      account: account,
      ...tokenConfig,
      prompt: "none"
    };

    // Prøv kun silent token acquisition (ingen popup)
    const silentResult = await instance.acquireTokenSilent(tokenRequest);

    if (silentResult?.accessToken) {
      // Oppdater cache
      if (silentResult.expiresOn) {
        let expiresAt: number;
        if (silentResult.expiresOn instanceof Date) {
          expiresAt = silentResult.expiresOn.getTime();
        } else {
          expiresAt = new Date(silentResult.expiresOn).getTime();
        }
        tokenExpiresAt = expiresAt;
        cachedToken = silentResult;
      }
      return silentResult;
    }

    return null;
  } catch (error: any) {
    // Silent refresh feilet - logg men ikke vis popup
    console.debug('[Auth] Silent token refresh failed for SignalR:', error);
    return null;
  }
}

// Eksporter kun det som faktisk eksisterer
export {
  getAxiosClient,
  isUserGlobalAdmin,
  userAzureId,
  getTokenSilent,
  userAccount,
  login,
  logout,
  isAuthenticated,
  environment,
  initAuth,
  getEnvironmentVariables,
  configCache,
  hubURL,
  getTokenPopup
};
